import type {
  OrganizationSettingsResponse,
  BusinessHolidayResponse,
  CreateHolidayRequest,
} from "../../types/organizations";

// Form state for organization settings (mirrors the form state in the current implementation)
export interface OrgSettingsFormState {
  // Branding
  logoUrl: string;
  primaryColor: string;
  secondaryColor: string;
  // Email
  fromEmailPrefix: string;
  fromEmailName: string;
  inboundEmailEnabled: boolean;
  autoCreateCustomers: boolean;
  aiClassificationEnabled: boolean;
  // Customer Portal
  allowCustomerSelfRegistration: boolean;
  isKnowledgeBasePublic: boolean;
  articleReviewIntervalDays: number;
  // Ticket Settings
  autoCloseDays: number;
  requireCategoryOnResolve: boolean;
  // Business Hours
  timeZoneId: string;
  workDayStart: string;
  workDayEnd: string;
  workingDays: string[];
}

// Email validation state
export interface EmailValidationState {
  isValidating: boolean;
  isValid: boolean | null;
  error: string | null;
}

// Portal key status
export interface PortalKeyStatus {
  hasPortalKey: boolean;
  maskedPortalKey: string | null;
  createdAt: string | null;
}

// Pending holiday changes for explicit save pattern
export interface PendingHolidayChanges {
  toAdd: CreateHolidayRequest[];
  toRemove: string[]; // Holiday IDs to remove
}

// Tab values for the vertical tabs
export type OrgSettingsTab =
  | "general"
  | "email"
  | "portal"
  | "tickets"
  | "users"
  | "categories"
  | "collections"
  | "automation"
  | "deleted"
  | "held";

// Default form state values
export const DEFAULT_FORM_STATE: OrgSettingsFormState = {
  logoUrl: "",
  primaryColor: "#3B82F6",
  secondaryColor: "#6B7280",
  fromEmailPrefix: "",
  fromEmailName: "",
  inboundEmailEnabled: false,
  autoCreateCustomers: false,
  aiClassificationEnabled: false,
  allowCustomerSelfRegistration: true,
  isKnowledgeBasePublic: true,
  articleReviewIntervalDays: 180,
  autoCloseDays: 7,
  requireCategoryOnResolve: false,
  timeZoneId: "UTC",
  workDayStart: "09:00",
  workDayEnd: "17:00",
  workingDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
};

// Helper to create form state from API response
export function createFormStateFromSettings(
  settings: OrganizationSettingsResponse
): OrgSettingsFormState {
  const fromEmailPrefix = settings.fromEmailAddress
    ? settings.fromEmailAddress.replace("@euserviceline.com", "")
    : "";

  return {
    logoUrl: settings.logoUrl || "",
    primaryColor: settings.primaryColor || "#3B82F6",
    secondaryColor: settings.secondaryColor || "#6B7280",
    fromEmailPrefix,
    fromEmailName: settings.fromEmailName || "",
    inboundEmailEnabled: settings.inboundEmailEnabled ?? false,
    autoCreateCustomers: settings.autoCreateCustomers ?? false,
    aiClassificationEnabled: settings.aiClassificationEnabled ?? false,
    allowCustomerSelfRegistration:
      settings.allowCustomerSelfRegistration ?? true,
    isKnowledgeBasePublic: settings.isKnowledgeBasePublic ?? true,
    articleReviewIntervalDays: settings.articleReviewIntervalDays ?? 180,
    autoCloseDays: settings.autoCloseDays ?? 7,
    requireCategoryOnResolve: settings.requireCategoryOnResolve ?? false,
    timeZoneId: settings.timeZoneId || "UTC",
    workDayStart: settings.workDayStart || "09:00",
    workDayEnd: settings.workDayEnd || "17:00",
    workingDays: settings.workingDays || [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
    ],
  };
}

// Computed holiday list that merges server data with pending changes
export function computeDisplayHolidays(
  serverHolidays: BusinessHolidayResponse[] | undefined,
  pendingChanges: PendingHolidayChanges
): Array<
  BusinessHolidayResponse & { isPending?: boolean; isRemoving?: boolean }
> {
  const result: Array<
    BusinessHolidayResponse & { isPending?: boolean; isRemoving?: boolean }
  > = [];

  // Add existing holidays, marking removed ones
  if (serverHolidays) {
    for (const holiday of serverHolidays) {
      const isRemoving = pendingChanges.toRemove.includes(holiday.id);
      result.push({ ...holiday, isRemoving });
    }
  }

  // Add pending new holidays with temporary IDs
  for (let i = 0; i < pendingChanges.toAdd.length; i++) {
    const pending = pendingChanges.toAdd[i];
    result.push({
      id: `pending-${i}`,
      name: pending.name,
      date: pending.date,
      isRecurring: pending.isRecurring ?? false,
      observeNearestWeekday: pending.observeNearestWeekday ?? false,
      createdAt: new Date().toISOString(),
      isPending: true,
    });
  }

  return result;
}
